import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  IconButton,
  Skeleton,
  Stack,
  Text,
  VStack,
  Wrap,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";

import { Elements } from "@stripe/react-stripe-js";
import Avatar from "boring-avatars";
import { Helmet } from "react-helmet";
import { CiCreditCardOff } from "react-icons/ci";
import { IoMdLogOut } from "react-icons/io";
import { PiXBold } from "react-icons/pi";
import Stripe from "stripe";
import { stripePromise } from "..";
import { UserContext } from "../App";
import EmptyState from "../components/EmptyState";
import SetupPaymentMethod from "../components/SetupPaymentMethod";
import { UserService } from "../services/user.service";
import { IUser } from "../types/types";

const userService = new UserService();

const Profile: React.FC = () => {
  const { user } = useContext(UserContext);

  const toast = useToast();
  const {
    isOpen: isSetupPaymentMethodOpen,
    onOpen: onSetupPaymentMethodOpen,
    onClose: onSetupPaymentMethodClose,
  } = useDisclosure();

  const [stripeClientSecret, setStripeClientSecret] = useState<
    string | undefined
  >();
  const [userPaymentMethods, setUserPaymentMethods] = useState<
    Stripe.PaymentMethod[] | undefined
  >();
  const [userPaymentMethodsLoading, setUserPaymentMethodsLoading] =
    useState<boolean>(false);
  const [removePaymentMethodsLoading, setRemovePaymentMethodsLoading] =
    useState<{
      [id: string]: boolean;
    }>({});

  useEffect(() => {
    getStripeClientSecret();
    getUserPaymentMethods();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getStripeClientSecret = async () => {
    if (user) {
      userService
        .create_setup_intent()
        .then(({ data }) => setStripeClientSecret(data.client_secret))
        .catch((err) => {
          toast({
            title: "Ha ocurrido un error",
            description:
              "Ha ocurrido un error inesperado. Por favor, recarga la página e inténtalo de nuevo",
            status: "error",
            duration: 5000,
            position: "top",
            isClosable: true,
          });
        });
    }
  };

  const getUserPaymentMethods = async () => {
    setUserPaymentMethodsLoading(true);

    await userService
      .payment_methods()
      .then(({ data }) => setUserPaymentMethods(data.payment_methods))
      .catch((err) => {
        toast({
          title: "Error al obtener tarjetas",
          description: "Ha ocurrido un error al obtener las tarjetas",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setUserPaymentMethodsLoading(false);
      });
  };

  const deleteUser = () => {
    userService
      .delete()
      .then(({ data }) => {
        window.location.reload();
      })
      .catch((err) => {
        toast({
          title: "Error al borrar el usuario",
          description: "Ha ocurrido un error al borrar el usuario",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      });
  };

  const onSetupPaymentMethodSuccess = () => {
    onSetupPaymentMethodClose();
    getUserPaymentMethods();
  };

  const removePaymentMethod = (paymentMethodId: string) => {
    setRemovePaymentMethodsLoading({
      ...removePaymentMethodsLoading,
      [paymentMethodId]: true,
    });

    userService
      .remove_payment_method(paymentMethodId)
      .then(({ data }) => {
        getUserPaymentMethods();

        toast({
          title: "Tarjeta eliminada",
          description: "Se ha eliminado la tarjeta correctamente",
          status: "success",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .catch(({ response }) => {
        if (response.data.code === "ERR_ACTIVE_BOOKING") {
          toast({
            title: "Error al eliminar tarjeta",
            description:
              "No puedes eliminar una tarjeta con una reserva activa",
            status: "error",
            duration: 5000,
            position: "top",
            isClosable: true,
          });
        }
      })
      .finally(() => {
        setRemovePaymentMethodsLoading({
          ...removePaymentMethodsLoading,
          [paymentMethodId]: false,
        });
      });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Mi perfil - SALA</title>
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <VStack w={"full"} spacing={16} alignItems={"start"} p={4}>
        <VStack
          spacing={8}
          justifyContent={"start"}
          alignItems={"start"}
          w={"full"}
          h={"fit-content"}
          textAlign={"start"}
        >
          <Heading as="h1">Mi perfil</Heading>

          <Stack
            direction={{
              base: "column",
              md: "row",
            }}
            spacing={8}
            justifyContent={"space-between"}
            alignItems={"start"}
            w={"full"}
          >
            <HStack spacing={8} justifyContent={"start"} alignItems={"start"}>
              <Avatar
                size={80}
                variant="marble"
                name={(user as IUser).sala_user.email}
              />

              <VStack spacing={2} justifyContent={"start"} alignItems={"start"}>
                <Heading as="h2" size={"lg"}>
                  {(user as IUser).sala_user.first_name}{" "}
                  {(user as IUser).sala_user.last_name}
                </Heading>
                <VStack
                  spacing={0}
                  justifyContent={"start"}
                  alignItems={"start"}
                >
                  <Text fontSize="md" color={"gray.600"}>
                    {(user as IUser).sala_user.email}
                  </Text>
                  <Text fontSize="md" color={"gray.600"}>
                    {(user as IUser).sala_user.phone_number}
                  </Text>
                </VStack>

                <Text fontSize="md" color={"gray.600"}>
                  ¡En SALA desde el{" "}
                  {new Date(
                    (user as IUser).sala_user.created_at
                  ).toLocaleDateString("es-ES")}
                  !
                </Text>

                <Button
                  mt={4}
                  colorScheme="red"
                  size={"sm"}
                  variant={"outline"}
                  rightIcon={<Icon as={IoMdLogOut} />}
                  onClick={() => {
                    localStorage.removeItem("jwt");
                    window.location.reload();
                  }}
                >
                  Cerrar sesión
                </Button>
              </VStack>
            </HStack>
          </Stack>

          <Accordion w={"full"}>
            <AccordionItem>
              <h2>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Información del pago
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Skeleton
                  isLoaded={!userPaymentMethodsLoading}
                  as={VStack}
                  spacing={6}
                  align={"flex-start"}
                  w={"full"}
                >
                  {userPaymentMethods && userPaymentMethods.length > 0 ? (
                    <>
                      <Wrap gap={12} w={"full"}>
                        {userPaymentMethods.map((paymentMethod) => (
                          <Box
                            key={paymentMethod.id}
                            bgColor={"gray.100"}
                            borderRadius={"lg"}
                            p={4}
                            pb={12}
                            h={"130px"}
                            aspectRatio={2.1}
                            w={"full"}
                            maxW={"370px"}
                          >
                            <HStack
                              spacing={4}
                              justifyContent={"space-between"}
                              alignItems={"start"}
                            >
                              <VStack alignItems={"flex-start"} spacing={2}>
                                <HStack>
                                  <Text lineHeight={1}>
                                    <b>···· {paymentMethod.card?.last4}</b>
                                  </Text>
                                </HStack>
                                <Text
                                  lineHeight={1}
                                  fontSize={"sm"}
                                  color={"gray.500"}
                                >
                                  Expira {paymentMethod.card!.exp_month}/
                                  {paymentMethod.card!.exp_year}
                                </Text>
                              </VStack>

                              <VStack alignItems={"flex-end"} spacing={4}>
                                <Text
                                  fontWeight={"bold"}
                                  size={"lg"}
                                  lineHeight={1}
                                >
                                  {paymentMethod.card!.brand[0].toUpperCase() +
                                    paymentMethod.card!.brand.slice(1)}
                                </Text>
                                <IconButton
                                  aria-label="Eliminar tarjeta"
                                  icon={<Icon as={PiXBold} />}
                                  size={"sm"}
                                  variant={"outline"}
                                  colorScheme={"red"}
                                  isLoading={
                                    removePaymentMethodsLoading[
                                      paymentMethod.id
                                    ]
                                  }
                                  onClick={() => {
                                    removePaymentMethod(paymentMethod.id);
                                  }}
                                />
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </Wrap>

                      <Button
                        colorScheme="brand"
                        size={"sm"}
                        mt={2}
                        onClick={() => onSetupPaymentMethodOpen()}
                        isDisabled={!stripeClientSecret}
                      >
                        Añadir tarjeta
                      </Button>
                    </>
                  ) : (
                    <EmptyState
                      size="sm"
                      icon={{
                        color: "gray",
                        icon: CiCreditCardOff,
                      }}
                      title={"No tienes ninguna tarjeta guardada"}
                      description={
                        "Añade una tarjeta para poder realizar pagos en SALA."
                      }
                      action={{
                        label: "Añadir tarjeta",
                        onClick: () => onSetupPaymentMethodOpen(),
                        isDisabled: !stripeClientSecret,
                      }}
                    />
                  )}
                </Skeleton>

                {stripeClientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: stripeClientSecret,
                      loader: "always",
                      locale: "es",
                      appearance: {
                        variables: {
                          colorPrimary: "#AD31C1",
                          borderRadius: "16px",
                        },
                      },
                    }}
                  >
                    <SetupPaymentMethod
                      isOpen={isSetupPaymentMethodOpen}
                      onClose={onSetupPaymentMethodClose}
                      onSuccess={onSetupPaymentMethodSuccess}
                    />
                  </Elements>
                )}
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem>
              <h2>
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    Zona de peligro
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel
                pb={4}
                as={VStack}
                spacing={4}
                justifyContent={"start"}
                alignItems={"start"}
              >
                <Text color={"gray.800"}>
                  ¡Cuidado! Esta sección es peligrosa. Si no sabes lo que estás
                  haciendo, ¡no toques nada!
                </Text>
                <Button colorScheme="red" onClick={deleteUser} size={"sm"}>
                  Borrar mi perfil
                </Button>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </VStack>
      </VStack>
    </>
  );
};

export default Profile;
